/**
 * School profile facts card.
 *
 * What: Compact card showing school address, zone, type, session, MRT, and bus info.
 * Why: Surfaces factual school data above the fold so the page is useful beyond the DeepThink pitch.
 * When: Update when the school data shape changes or new fields are added.
 */
import React from 'react';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';

import type { SchoolRecord } from '@shared/schools/schoolData.js';
import { colors, radii, shadows } from '@/components/landing/landingTokens';
import { getSchoolNatureText } from '@/components/schools/schoolProfileFacts.js';

interface SchoolProfileCardProps {
  school: SchoolRecord;
}

function formatTypeCode(typeCode: string): string {
  return typeCode
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function formatSessionCode(sessionCode: string): string {
  return sessionCode
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

interface ProfileRowProps {
  label: string;
  value: string;
}

function ProfileRow({ label, value }: ProfileRowProps) {
  if (!value) return null;
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={{ xs: 0.25, sm: 1.5 }}
    >
      <Typography
        sx={{
          fontSize: '0.875rem',
          fontWeight: 600,
          color: colors.inkSubtle,
          minWidth: { sm: 96 },
          flexShrink: 0,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: '0.875rem',
          color: colors.ink,
          lineHeight: 1.5,
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

function SchoolProfileCard({ school }: SchoolProfileCardProps) {
  const addressLine = [
    school.address,
    school.postalCode ? `Singapore ${school.postalCode}` : '',
  ]
    .filter(Boolean)
    .join(', ');
  const natureValue = school.natureCode ? getSchoolNatureText(school) : '';

  const badges: string[] = [];
  if (school.isInternational) badges.push('International School');
  if (school.autonomousInd) badges.push('Autonomous');
  if (school.sapInd) badges.push('SAP');
  if (school.giftedInd) badges.push('GEP');
  if (school.ipInd) badges.push('IP');

  return (
    <Paper
      elevation={0}
      component="aside"
      aria-label={`${school.displayName} school profile`}
      sx={{
        border: `1px solid ${colors.border}`,
        borderRadius: radii.md,
        backgroundColor: colors.paper,
        boxShadow: shadows.card,
        p: { xs: 2.5, sm: 3 },
      }}
    >
      <Stack spacing={2}>
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Typography
            component="h2"
            sx={{
              fontSize: { xs: '1rem', sm: '1.0625rem' },
              fontWeight: 700,
              color: colors.ink,
            }}
          >
            School profile
          </Typography>
          {badges.length > 0 && (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {badges.map(badge => (
                <Chip
                  key={badge}
                  label={badge}
                  size="small"
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    backgroundColor: colors.washAmber,
                    // Orange-on-amber-wash needs the dark step to clear AA.
                    color: colors.accentOrangeDark,
                    height: 24,
                  }}
                />
              ))}
            </Stack>
          )}
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: { xs: 1.5, sm: 2 },
          }}
        >
          {addressLine && <ProfileRow label="Address" value={addressLine} />}
          {school.zone && (
            <ProfileRow
              label="Zone"
              value={`${school.zone.charAt(0)}${school.zone.slice(1).toLowerCase()}`}
            />
          )}
          {school.typeCode && (
            <ProfileRow label="Type" value={formatTypeCode(school.typeCode)} />
          )}
          {natureValue && <ProfileRow label="Nature" value={natureValue} />}
          {school.sessionCode && (
            <ProfileRow
              label="Session"
              value={formatSessionCode(school.sessionCode)}
            />
          )}
          {school.nearestMrt && (
            <ProfileRow label="MRT" value={school.nearestMrt} />
          )}
          {school.busInfo && <ProfileRow label="Bus" value={school.busInfo} />}
          {school.phone && <ProfileRow label="Phone" value={school.phone} />}
          {school.email && <ProfileRow label="Email" value={school.email} />}
        </Box>

        {/^https?:\/\//i.test(school.websiteUrl ?? '') && (
          <Typography sx={{ fontSize: '0.8125rem' }}>
            <a
              href={school.websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                // 13px body-scale link: the dark step is the only orange that
                // clears AA on paper.
                color: colors.accentOrangeDark,
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              Official school website ↗
            </a>
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}

export default SchoolProfileCard;
